import { createServerFn } from "@tanstack/react-start";
import { getRequestHeader } from "@tanstack/react-start/server";
import { createClient } from "@supabase/supabase-js";
import { z } from "zod";
import type { Database } from "@/integrations/supabase/types";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

async function ensureAdmin(ctx: { supabase: any; userId: string }) {
  const { data, error } = await ctx.supabase.rpc("has_role", {
    _user_id: ctx.userId,
    _role: "admin",
  });
  if (error || !data) throw new Error("Forbidden");
}

const subSchema = z.object({
  endpoint: z.string().url().max(600),
  p256dh: z.string().min(10).max(200),
  auth: z.string().min(10).max(100),
  country: z.string().min(2).max(30),
  city_slug: z.string().max(60).optional().nullable(),
  metal: z.enum(["gold", "silver"]),
  threshold_pkr: z.number().int().min(100).max(1000000),
});

const unsubSchema = z.object({ endpoint: z.string().url().max(600) });

const saveSchema = z.object({
  endpoint: z.string().url().max(600),
  p256dh: z.string().min(10).max(200),
  auth: z.string().min(10).max(100),
  userAgent: z.string().max(255).optional(),
});

const broadcastSchema = z.object({
  title: z.string().trim().min(1).max(120),
  body: z.string().trim().min(1).max(300),
  url: z.string().max(500).default("/"),
  country: z.string().max(30).optional().nullable(),
  metal: z.enum(["gold", "silver"]).optional().nullable(),
  // Deep-link fields — when highlightId provided, url is auto-composed as
  // /{countrySlug}?highlight=<id>&city=<slug> so notification clicks land
  // on the exact comment with the golden-pulse effect.
  highlightId: z.string().max(64).optional().nullable(),
  citySlug: z.string().max(60).optional().nullable(),
  countrySlug: z.string().max(30).optional().nullable(),
});

async function hashInput(input: string): Promise<string> {
  const buf = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest("SHA-256", buf);
  return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, "0")).join("").slice(0, 32);
}

function serverClient() {
  return createClient<Database>(
    process.env.SUPABASE_URL!,
    process.env.SUPABASE_PUBLISHABLE_KEY!,
    { auth: { storage: undefined, persistSession: false, autoRefreshToken: false } },
  );
}

export const subscribePush = createServerFn({ method: "POST" })
  .inputValidator((d) => subSchema.parse(d))
  .handler(async ({ data }) => {
    const ua = getRequestHeader("user-agent") || "";
    const ua_hash = await hashInput(`ua:${ua}`);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const args = {
      _endpoint: data.endpoint,
      _p256dh: data.p256dh,
      _auth: data.auth,
      _country: data.country,
      _city_slug: data.city_slug ?? null,
      _metal: data.metal,
      _threshold_pkr: data.threshold_pkr,
      _ua_hash: ua_hash,
    } as never;
    const { data: id, error } = await supabaseAdmin.rpc("push_subscribe", args);
    if (error) return { ok: false as const, error: error.message };
    return { ok: true as const, id: id as unknown as string };
  });

export const unsubscribePush = createServerFn({ method: "POST" })
  .inputValidator((d) => unsubSchema.parse(d))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await supabaseAdmin.rpc("push_unsubscribe", { _endpoint: data.endpoint } as never);
    if (error) return { ok: false as const };
    return { ok: true as const };
  });

// --- Legacy generic push_subscriptions (admin broadcast) ---

export const savePushSubscription = createServerFn({ method: "POST" })
  .inputValidator((d) => saveSchema.parse(d))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await supabaseAdmin
      .from("push_subscriptions")
      .upsert(
        {
          endpoint: data.endpoint,
          p256dh: data.p256dh,
          auth: data.auth,
          user_agent: data.userAgent ?? null,
        },
        { onConflict: "endpoint" },
      );
    if (error) return { ok: false as const, error: error.message };
    return { ok: true as const };
  });

export const getSubscriberCount = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
  await ensureAdmin(context);
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const [a, b] = await Promise.all([
    supabaseAdmin.from("push_subscribers").select("id", { count: "exact", head: true }).eq("active", true),
    supabaseAdmin.from("push_subscriptions").select("id", { count: "exact", head: true }),
  ]);
  return { count: (a.count ?? 0) + (b.count ?? 0), price_alert: a.count ?? 0, legacy: b.count ?? 0 };
});

export const getSubscriberStats = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
  await ensureAdmin(context);
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const { data, error } = await supabaseAdmin
    .from("push_subscribers")
    .select("country, metal, active, created_at")
    .eq("active", true);
  if (error) return { total: 0, byCountry: {} as Record<string, number>, byMetal: {} as Record<string, number>, last24h: 0 };
  const byCountry: Record<string, number> = {};
  const byMetal: Record<string, number> = {};
  const now = Date.now();
  let last24h = 0;
  for (const r of data ?? []) {
    const c = (r as { country: string }).country;
    const m = (r as { metal: string }).metal;
    byCountry[c] = (byCountry[c] ?? 0) + 1;
    byMetal[m] = (byMetal[m] ?? 0) + 1;
    const t = new Date((r as { created_at: string }).created_at).getTime();
    if (now - t < 24 * 3600 * 1000) last24h += 1;
  }
  return { total: data?.length ?? 0, byCountry, byMetal, last24h };
});

export const broadcastPush = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => broadcastSchema.parse(d))
  .handler(async ({ data, context }) => {
    await ensureAdmin(context);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { sendPushToAll } = await import("./push.server");

    // Primary: price-alert subscribers (with country/metal filter)
    let q = supabaseAdmin
      .from("push_subscribers")
      .select("endpoint, p256dh, auth")
      .eq("active", true);
    if (data.country) q = q.eq("country", data.country);
    if (data.metal) q = q.eq("metal", data.metal);
    const { data: primary } = await q;

    // Legacy generic subscribers (only when no filter set)
    let legacy: Array<{ endpoint: string; p256dh: string; auth: string }> = [];
    if (!data.country && !data.metal) {
      const { data: rows } = await supabaseAdmin
        .from("push_subscriptions")
        .select("endpoint, p256dh, auth");
      legacy = (rows ?? []) as typeof legacy;
    }

    // De-dup by endpoint
    const seen = new Set<string>();
    const subs: Array<{ endpoint: string; keys: { p256dh: string; auth: string } }> = [];
    for (const r of [...(primary ?? []), ...legacy]) {
      const ep = (r as { endpoint: string }).endpoint;
      if (seen.has(ep)) continue;
      seen.add(ep);
      subs.push({
        endpoint: ep,
        keys: {
          p256dh: (r as { p256dh: string }).p256dh,
          auth: (r as { auth: string }).auth,
        },
      });
    }

    let finalUrl = data.url;
    if (data.highlightId) {
      const c = (data.countrySlug || data.country || "pakistan").toLowerCase();
      const base = c === "saudi-arabia" ? "/ar/gold-rate-saudi-arabia" : `/${c}`;
      const qs = new URLSearchParams();
      qs.set("highlight", data.highlightId);
      if (data.citySlug) qs.set("city", data.citySlug);
      finalUrl = `${base}?${qs.toString()}#local-bazaar-widget`;
    }
    // Create broadcast row up-front so click-tracking has an id
    const { data: bcRow } = await supabaseAdmin
      .from("push_broadcasts" as never)
      .insert({
        title: data.title,
        body: data.body,
        url: finalUrl,
        country: data.country ?? null,
        metal: data.metal ?? null,
        target_count: subs.length,
      } as never)
      .select("id")
      .single();
    const broadcastId = (bcRow as { id?: string } | null)?.id ?? null;

    const payload = JSON.stringify({ title: data.title, body: data.body, url: finalUrl, bc: broadcastId });
    const result = await sendPushToAll(subs, payload);

    // Do NOT auto-deactivate stale endpoints — user preference: keep retrying
    // in case delivery succeeds later (device offline, browser closed, etc.).
    // Persist final counters on the broadcast row.
    if (broadcastId) {
      await supabaseAdmin
        .from("push_broadcasts" as never)
        .update({ sent: result.sent, failed: result.failed } as never)
        .eq("id", broadcastId);
    }
    return { ...result, total: subs.length, cleaned: 0, broadcastId };
  });

export type BroadcastRow = {
  id: string;
  title: string;
  body: string;
  url: string;
  country: string | null;
  metal: string | null;
  target_count: number;
  sent: number;
  failed: number;
  clicks: number;
  created_at: string;
};

// List recent broadcasts + click stats for the admin panel.
export const listRecentBroadcasts = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
  await ensureAdmin(context);
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const { data, error } = await supabaseAdmin
    .from("push_broadcasts" as never)
    .select("id, title, body, url, country, metal, target_count, sent, failed, clicks, created_at")
    .order("created_at", { ascending: false })
    .limit(30);
  if (error) return { rows: [] as BroadcastRow[] };
  return { rows: (data ?? []) as unknown as BroadcastRow[] };
});

// Returns the VAPID public key from server env so the browser always
// subscribes with the exact key the server will sign with. Prevents
// FCM 403 VapidPkHashMismatch errors that silently kill broadcasts.
export const getVapidPublicKey = createServerFn({ method: "GET" }).handler(async () => {
  return { key: process.env.VAPID_PUBLIC_KEY || "" };
});

// Send a single test notification to the caller's own endpoint — used from
// admin panel to confirm end-to-end delivery works.
export const sendTestPush = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => z.object({
    endpoint: z.string().url().max(600),
    p256dh: z.string().min(10).max(200),
    auth: z.string().min(10).max(100),
  }).parse(d))
  .handler(async ({ data, context }) => {
    await ensureAdmin(context);
    const { sendPushOne } = await import("./push.server");
    const payload = JSON.stringify({
      title: "✅ DrHint test notification",
      body: "Agar ye dikh raha hai to push delivery kaam kar rahi hai.",
      url: "/",
    });
    const r = await sendPushOne(
      { endpoint: data.endpoint, keys: { p256dh: data.p256dh, auth: data.auth } },
      payload,
    );
    return r;
  });